import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { isSafeMode } from '../lib/safeMode';
import { useAuth } from '../lib/auth';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { VideoCard } from '../components/VideoCard';
import { CreditCard, ListVideo, ShieldCheck, Clapperboard } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '../i18n';
import { translateCategory } from '../lib/categories';

export default function SeriesPage() {
    const { seriesId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t, locale } = useI18n();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState('all');

    const load = useCallback(async () => {
        try {
            const response = await api.get(`/series/${seriesId}`);
            if (response.data.series?.is_adult && isSafeMode()) {
                setError(t('series.load_error'));
                return;
            }
            setData(response.data);
            setError(null);
        } catch (requestError) {
            setError(requestError.response?.data?.detail || t('series.load_error'));
        }
    }, [seriesId, t]);

    useEffect(() => { load(); }, [load]);

    const buySeries = async () => {
        if (!user) { navigate('/entrar'); return; }
        try {
            const response = await api.post(`/series/${seriesId}/create-checkout-session`);
            if (response.data.url) { window.location.href = response.data.url; return; }
            toast.success(t('series.unlocked'));
            await load();
        } catch (requestError) {
            toast.error(requestError.response?.data?.detail || t('series.purchase_error'));
        }
    };

    if (error) return <main className="mx-auto max-w-2xl px-3 py-12"><Card className="p-6 text-center bg-[color:var(--alb-surface)]"><ShieldCheck className="mx-auto h-8 w-8 text-[color:var(--alb-sun)]" /><h1 className="mt-3 font-display text-2xl font-semibold">{t('series.restricted')}</h1><p className="mt-2 text-sm text-[color:var(--alb-text-muted)]">{error}</p><Button className="mt-5 bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]" onClick={() => navigate(user ? '/perfil' : '/entrar')}>{user ? t('series.configure_access') : t('auth.submit_login')}</Button></Card></main>;
    if (!data) return <main className="mx-auto max-w-[1400px] px-3 py-8"><div className="h-48 rounded bg-[color:var(--alb-surface)] alb-pulse" /></main>;

    const series = data.series;
    const languageLabel = (code) => {
        if (!code || code === 'all') return t('series.language_all');
        const localized = t(`lang.${code}`);
        return localized === `lang.${code}` ? code.toUpperCase() : localized;
    };
    const languageOptions = ['all', ...Array.from(new Set((data.episodes || []).map((episode) => episode.content_language || 'ca')))]
        .sort((a, b) => (a === 'all' ? -1 : b === 'all' ? 1 : languageLabel(a).localeCompare(languageLabel(b), locale)));
    const visibleEpisodes = selectedLanguage === 'all'
        ? data.episodes
        : data.episodes.filter((episode) => (episode.content_language || 'ca') === selectedLanguage);
    const hasFreeEpisodes = (data.episodes || []).some((episode) => !episode.is_premium);

    return <main className="mx-auto max-w-[1400px] px-3 sm:px-5 lg:px-8 py-6 md:py-10 pb-24">
        <header className="border-b border-[color:var(--alb-hairline)] pb-6">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
                <div className="min-w-0">
                    <div className="flex flex-wrap gap-2"><Badge>{translateCategory(series.category, locale)}</Badge>{series.is_adult && <Badge variant="destructive">+18</Badge>}{series.is_premium && <Badge variant="secondary">Premium</Badge>}{hasFreeEpisodes && <Badge variant="outline">Capítols gratuïts</Badge>}</div>
                    <h1 className="mt-3 font-display text-3xl font-semibold md:text-4xl">{series.title}</h1>
                    {series.description && <p className="mt-3 max-w-3xl text-[color:var(--alb-text-muted)]">{series.description}</p>}
                    <p className="mt-3 flex items-center gap-2 text-sm text-[color:var(--alb-text-muted)]"><ListVideo className="h-4 w-4" /> {t('series.episode_count', { n: data.episodes.length })}</p>
                </div>
                {series.is_premium && series.series_price && !data.purchased && <Button onClick={buySeries} className="shrink-0 bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]"><CreditCard className="mr-2 h-4 w-4" /> {t('series.buy')} · {Number(series.series_price).toFixed(2)} EUR</Button>}
            </div>
        </header>
        {(data.trailers || []).length > 0 && (
            <section className="mt-6">
                <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold"><Clapperboard className="h-5 w-5 text-[color:var(--alb-sun)]" /> Tràiler promocional</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">{data.trailers.map((trailer) => <div key={trailer.video_id}><VideoCard video={trailer} /></div>)}</div>
            </section>
        )}
        <section className="mt-6">
            <div className="mb-4 max-w-xs">
                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                    <SelectTrigger className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]"><SelectValue placeholder={t('series.filter_language')} /></SelectTrigger>
                    <SelectContent>
                        {languageOptions.map((code) => <SelectItem key={code} value={code}>{languageLabel(code)}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            {visibleEpisodes.length === 0 ? <p className="text-sm text-[color:var(--alb-text-muted)]">{t('series.no_episodes')}</p> : <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">{visibleEpisodes.map((episode) => <div key={episode.video_id}><div className="mb-2 flex items-center gap-2 text-xs font-semibold text-[color:var(--alb-sun)]"><span>{t('series.episode_number', { n: episode.episode_number })}</span><span className="rounded border border-[color:var(--alb-hairline)] px-2 py-0.5 text-[color:var(--alb-text-muted)]">{languageLabel(episode.content_language || 'ca')}</span>{episode.is_premium ? <span className="rounded bg-[color:var(--alb-sun)] px-2 py-0.5 text-[color:var(--alb-ink)]">Premium · {Number(episode.price || 0).toFixed(2)} EUR</span> : series.is_premium ? <span className="rounded border border-[color:var(--alb-hairline)] px-2 py-0.5 text-[color:var(--alb-text-muted)]">Gratuït</span> : null}</div><VideoCard video={episode} /></div>)}</div>}
        </section>
    </main>;
}
